import React, { useState, useRef, useEffect, useMemo, JSX } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import GlobalHeaderTitle from '../../components/GlobalHeaderTitle';
import { useExamTypes } from '../../stateManagers/hooks/useExamTypes';
import useClassesStore from '../../stateManagers/store/useClassesStore';
import { AwardStudentRow } from './parts/awardTypes';
import { SubjectGroup } from './parts/subjectGroupTypes';
import SubjectCombinerModal from './parts/SubjectCombinerModal';
import SubjectExcluderModal from './parts/SubjectExcluderModal';
import PrintOptionsModal from './parts/PrintOptionsModal';
import StudentsListSkeletonLoader from '../../skeletons/StudentsListSkeletonLoader';
import {
  Orientation,
  PaperSize,
  PrintDisplayOptions,
  loadSubjectGroups,
  saveSubjectGroups,
  loadSubjectExclusions,
  saveSubjectExclusions,
  getAllSubjects,
  buildDisplaySubjects,
  applyExclusionsToSubjects,
  buildClassResultHtml,
  buildStudentReportCardHtml,
  applyPrintOptions,
  printHtml,
} from './parts/PrintService';
import { useOptions } from '../../stateManagers/hooks/useOptions';

const AwardRole: React.FC = () => {
  const toast = useRef<Toast>(null);

  const {
    examTypes,
    loading: examTypesLoading,
    error: examTypesError,
    selectedExamTypeId,
    setSelectedExamTypeId,
  } = useExamTypes();

  const { classOptions, fetchClasses, isFetching: isFetchingClasses, hasFetched } = useClassesStore();

  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [studentRows, setStudentRows] = useState<AwardStudentRow[]>([]);
  const [totalSubjects, setTotalSubjects] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasFetchedMarks, setHasFetchedMarks] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const [subjectGroups, setSubjectGroups] = useState<SubjectGroup[]>([]);
  const [excludedSubjectIds, setExcludedSubjectIds] = useState<number[]>([]);
  const [combinerOpen, setCombinerOpen] = useState(false);
  const [excluderOpen, setExcluderOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [printTarget, setPrintTarget] = useState<AwardStudentRow | 'class'>('class');
  const logoUrl = useSchoolLogo();
  const { options } = useOptions<string[]>('school_name');
  const schoolName = options['school_name']?.data?.[0] ?? undefined;

  useEffect(() => {
    if (!hasFetched && !isFetchingClasses) {
      fetchClasses(false);
    }
  }, [hasFetched, isFetchingClasses, fetchClasses]);

  function useSchoolLogo() {
    const [logoUrl, setLogoUrl] = useState<string | null>(null);

    useEffect(() => {
      apiClient.get('/options-files/school_logo')
        .then((res) => {
          setLogoUrl(res.data?.files?.[0]?.cdn_url ?? null);
        })
        .catch(() => {});
    }, []);

    return logoUrl;
  }

  useEffect(() => {
    if (!selectedClassId) {
      setSubjectGroups([]);
      setExcludedSubjectIds([]);
      return;
    }
    setSubjectGroups(loadSubjectGroups(selectedClassId));
    setExcludedSubjectIds(loadSubjectExclusions(selectedClassId));
  }, [selectedClassId]);

  const handleSubjectGroupsChange = (groups: SubjectGroup[]) => {
    setSubjectGroups(groups);
    if (selectedClassId) saveSubjectGroups(selectedClassId, groups);
  };

  const handleExcludedSubjectsChange = (ids: number[]) => {
    setExcludedSubjectIds(ids);
    if (selectedClassId) saveSubjectExclusions(selectedClassId, ids);
  };

  const selectedExamType = examTypes.find((t) => t.value === selectedExamTypeId);
  const selectedClass = classOptions.find((c) => c.id === selectedClassId);
  const hasSections = Boolean(selectedClass?.sections && selectedClass.sections.length > 0);
  const sections = selectedClass?.sections || [];

  const handleExamTypeChange = (examTypeId: string) => {
    setSelectedExamTypeId(examTypeId);
    setSelectedClassId(null);
    setSelectedSection(null);
    setStudentRows([]);
    setTotalSubjects(0);
    setHasFetchedMarks(false);
    setSearchQuery('');
  };

  const handleClassChange = (classId: string) => {
    setSelectedClassId((prev) => (prev === classId ? null : classId));
    setSelectedSection(null);
    setSearchQuery('');
  };

  const handleSectionChange = (section: string) => {
    setSelectedSection((prev) => (prev === section ? null : section));
    setSearchQuery('');
  };

  const fetchMarks = async (): Promise<void> => {
    if (!selectedExamTypeId || !selectedClassId) {
      setStudentRows([]);
      setTotalSubjects(0);
      setHasFetchedMarks(false);
      return;
    }
    setLoading(true);
    try {
      const params: any = {
        class_id: selectedClassId,
        exam_type_id: selectedExamTypeId,
      };
      if (selectedSection) params.section = selectedSection;

      const response = await apiClient.get('/admin/marks/class-award', { params });
      const data = response.data;

      if (data.status === 'success') {
        setStudentRows(data.data || []);
        setTotalSubjects(data.total_subjects || 0);
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: data.message || 'Failed to fetch marks',
          life: 4000,
        });
        setStudentRows([]);
        setTotalSubjects(0);
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Network Error',
        detail: err.response?.data?.message || 'An error occurred while fetching marks',
        life: 5000,
      });
      setStudentRows([]);
      setTotalSubjects(0);
    } finally {
      setLoading(false);
      setHasFetchedMarks(true);
    }
  };

  useEffect(() => {
    fetchMarks();
  }, [selectedExamTypeId, selectedClassId, selectedSection]);

  const filteredStudentRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const base = q
      ? studentRows.filter(
          (row) =>
            row.student_name?.toLowerCase().includes(q) ||
            String(row.student_roll_no).toLowerCase().includes(q)
        )
      : studentRows;

    return [...base].sort((a, b) => {
      const rollA = Number(a.student_roll_no);
      const rollB = Number(b.student_roll_no);
      if (!Number.isNaN(rollA) && !Number.isNaN(rollB)) return rollA - rollB;
      return String(a.student_roll_no).localeCompare(String(b.student_roll_no));
    });
  }, [studentRows, searchQuery]);

  const classStats = useMemo(() => {
    const totalStudents = studentRows.length;
    let fullyRecorded = 0;
    let partiallyRecorded = 0;
    let notRecorded = 0;
    let percentageSum = 0;
    let percentageCount = 0;
    let marksRecordedCount = 0;

    studentRows.forEach((row) => {
      if (row.total_subjects > 0 && row.subjects_with_marks === row.total_subjects) {
        fullyRecorded += 1;
      } else if (row.subjects_with_marks > 0) {
        partiallyRecorded += 1;
      } else {
        notRecorded += 1;
      }
      if (row.percentage !== null) {
        percentageSum += row.percentage;
        percentageCount += 1;
      }
      marksRecordedCount += row.subjects_with_marks;
    });

    const totalSlots = totalStudents * totalSubjects;
    const marksPendingCount = Math.max(totalSlots - marksRecordedCount, 0);
    const completionPercentage = totalSlots > 0 ? (marksRecordedCount / totalSlots) * 100 : null;

    return {
      totalStudents,
      totalSubjects,
      fullyRecorded,
      partiallyRecorded,
      notRecorded,
      marksRecordedCount,
      marksPendingCount,
      totalSlots,
      completionPercentage,
      averagePercentage: percentageCount > 0 ? percentageSum / percentageCount : null,
    };
  }, [studentRows, totalSubjects]);

  const subjectStats = useMemo(() => {
    const subjectMap = new Map<number, { hasAnyMarks: boolean }>();

    studentRows.forEach((row) => {
      row.subjects.forEach((subj) => {
        const existing = subjectMap.get(subj.subject_id);
        if (existing) {
          if (subj.has_marks) existing.hasAnyMarks = true;
        } else {
          subjectMap.set(subj.subject_id, { hasAnyMarks: subj.has_marks });
        }
      });
    });

    const subjectsTouched = Array.from(subjectMap.values()).filter((s) => s.hasAnyMarks).length;
    const subjectsUntouched = subjectMap.size - subjectsTouched;

    return {
      subjectsTouched,
      subjectsUntouched,
      totalSubjects: subjectMap.size,
    };
  }, [studentRows]);

  const allSubjects = useMemo(() => getAllSubjects(studentRows), [studentRows]);
  const displaySubjects = useMemo(() => {
    const grouped = buildDisplaySubjects(allSubjects, subjectGroups);
    return applyExclusionsToSubjects(grouped, excludedSubjectIds);
  }, [allSubjects, subjectGroups, excludedSubjectIds]);

  const openClassPrint = () => {
    setPrintTarget('class');
    setPrintOpen(true);
  };

  const openStudentPrint = (row: AwardStudentRow) => {
    setPrintTarget(row);
    setPrintOpen(true);
  };

  const handlePrint = (opts: { orientation: Orientation; paperSize: PaperSize; displayOptions: PrintDisplayOptions }) => {
    const examLabel = selectedExamType?.label ?? '';
    const className = selectedClass?.label ?? '';
    const html = printTarget === 'class'
      ? buildClassResultHtml(
          studentRows, displaySubjects, subjectGroups, excludedSubjectIds,
          className, selectedSection, examLabel, schoolName, logoUrl ?? undefined, opts.displayOptions,
        )
      : buildStudentReportCardHtml(
          printTarget, displaySubjects, subjectGroups, excludedSubjectIds,
          className, selectedSection, examLabel, schoolName, logoUrl ?? undefined, opts.displayOptions,
        );
    printHtml(applyPrintOptions(html, opts.paperSize, opts.orientation));
  };

  const getEmptyStateContent = () => {
    if (searchQuery.trim())
      return {
        icon: 'pi pi-search',
        title: 'No Results Found',
        message: `No students matched "${searchQuery}".`,
      };
    if (!selectedExamTypeId)
      return {
        icon: 'pi pi-filter',
        title: 'No Exam Type Selected',
        message: 'Please select an exam type from the filter above to view the merit list',
      };
    if (!selectedClassId)
      return {
        icon: 'pi pi-filter',
        title: 'No Class Selected',
        message: 'Please select a class from the filter above to view the merit list',
      };
    return {
      icon: 'pi pi-inbox',
      title: 'No Marks Found',
      message: `No exam marks have been recorded yet for this class${selectedSection ? ' / section' : ''} and exam type`,
    };
  };

  const rankTemplate = (row: AwardStudentRow): JSX.Element => {
    if (row.rank === null) {
      return (
        <div
          style={{
            width: 28,
            height: 28,
            background: '#f9fafb',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            color: '#cbd5e1',
            margin: '0 auto',
          }}
        >
          —
        </div>
      );
    }
    const isTop3 = row.rank <= 3;
    const gradients: Record<number, string> = {
      1: 'linear-gradient(135deg,#fbbf24,#f59e0b)',
      2: 'linear-gradient(135deg,#cbd5e1,#94a3b8)',
      3: 'linear-gradient(135deg,#fb923c,#c2410c)',
    };
    return (
      <div
        style={{
          width: 28,
          height: 28,
          background: isTop3 ? gradients[row.rank] : '#f9fafb',
          borderRadius: isTop3 ? '50%' : 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: 12,
          color: isTop3 ? 'white' : '#6b7280',
          margin: '0 auto',
          boxShadow: isTop3 ? '0 2px 6px rgba(0,0,0,0.15)' : 'none',
        }}
      >
        {row.rank}
      </div>
    );
  };

  const studentNameTemplate = (row: AwardStudentRow): JSX.Element => {
    const isTop3 = row.rank !== null && row.rank <= 3;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {row.student_profile_picture ? (
          <img
            src={row.student_profile_picture}
            alt={row.student_name}
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              objectFit: 'cover',
              boxShadow: isTop3 ? '0 0 0 2px #fbbf24' : 'none',
            }}
          />
        ) : (
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: '#dbeafe',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 700,
              color: '#1e40af',
              boxShadow: isTop3 ? '0 0 0 2px #fbbf24' : 'none',
            }}
          >
            {row.student_name?.charAt(0)?.toUpperCase()}
          </div>
        )}
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: 13,
              color: '#1f2937',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {row.student_name}
          </div>
        </div>
      </div>
    );
  };

  const rollNoTemplate = (row: AwardStudentRow): JSX.Element => (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 32,
        padding: '4px 8px',
        borderRadius: 6,
        background: '#f9fafb',
        fontSize: 12,
        fontWeight: 700,
        color: '#1f2937',
        border: '1px solid #e5e7eb',
      }}
    >
      {row.student_roll_no}
    </div>
  );

  const marksRecordedTemplate = (row: AwardStudentRow): JSX.Element => {
    const complete = row.subjects_with_marks === row.total_subjects && row.total_subjects > 0;
    const partial = row.subjects_with_marks > 0 && !complete;
    return (
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '3px 8px',
          borderRadius: 8,
          background: complete ? '#dcfce7' : partial ? '#fef9c3' : '#f3f4f6',
          color: complete ? '#166534' : partial ? '#854d0e' : '#6b7280',
          fontSize: 12,
          fontWeight: 700,
        }}
      >
        {complete ? (
          <i className="pi pi-check-circle" style={{ fontSize: 11 }} />
        ) : partial ? (
          <i className="pi pi-exclamation-circle" style={{ fontSize: 11 }} />
        ) : null}
        {row.subjects_with_marks} / {row.total_subjects}
      </div>
    );
  };

  const totalMarksTemplate = (row: AwardStudentRow): JSX.Element => (
    <div style={{ fontSize: 13, color: '#1f2937', fontWeight: 700, fontFamily: 'monospace' }}>
      {row.total_max_marks > 0 ? `${row.total_obtained_marks} / ${row.total_max_marks}` : '—'}
    </div>
  );

  const percentageTemplate = (row: AwardStudentRow): JSX.Element => {
    if (row.percentage === null) {
      return (
        <div
          style={{
            display: 'inline-flex',
            padding: '4px 10px',
            borderRadius: 8,
            background: '#f3f4f6',
            color: '#9ca3af',
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          —
        </div>
      );
    }
    return (
      <div
        style={{
          display: 'inline-flex',
          padding: '3px 8px',
          borderRadius: 8,
          background: row.percentage >= 75 ? '#dcfce7' : row.percentage >= 40 ? '#fef9c3' : '#fee2e2',
          color: row.percentage >= 75 ? '#166534' : row.percentage >= 40 ? '#854d0e' : '#991b1b',
          fontSize: 12,
          fontWeight: 700,
        }}
      >
        {row.percentage.toFixed(1)}%
      </div>
    );
  };

  const printTemplate = (row: AwardStudentRow): JSX.Element => (
    <Button
      icon="pi pi-file-pdf"
      text
      rounded
      onClick={() => openStudentPrint(row)}
      tooltip="Print report card"
      tooltipOptions={{ position: 'left' }}
    />
  );

  const emptyState = getEmptyStateContent();

  const showStats = Boolean(selectedExamTypeId && selectedClassId && hasFetchedMarks && studentRows.length > 0);

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <div style={{ minHeight: '100vh', fontFamily: 'system-ui,-apple-system,sans-serif' }}>
        <style>{`
          @keyframes slideIn   { from { opacity:0; transform:translateX(-10px) } to { opacity:1; transform:translateX(0) } }
          @keyframes bulkBarIn { from { opacity:0; transform:translateY(-6px)  } to { opacity:1; transform:translateY(0) } }
          .p-datatable .p-datatable-tbody > tr { animation:slideIn .35s ease-out; transition:background .15s; }
          .p-datatable .p-datatable-tbody > tr:hover { background:rgba(65,105,225,.04)!important; box-shadow:inset 4px 0 0 0 #4169E1; }
          .p-datatable .p-datatable-thead > tr > th { background:#f9fafb!important; color:#6b7280!important; font-size:11px!important; font-weight:700!important; text-transform:uppercase!important; letter-spacing:.8px!important; padding:10px 12px!important; border-bottom:1px solid #e5e7eb!important; }
          .p-datatable .p-datatable-tbody > tr > td { padding:8px 12px!important; border-bottom:1px solid #f3f4f6!important; }
          .p-datatable .p-datatable-tbody > tr:last-child > td { border-bottom:none!important; }
          .chip-button { transition: all 0.2s ease; }
          .chip-button:hover:not(:disabled) { transform: translateY(-1px); }
          .chip-button:active:not(:disabled) { transform: translateY(0); }
          .search-input-wrapper { display:flex; align-items:center; gap:10px; background:white; border-radius:16px; padding:12px 18px; margin-bottom:20px; box-shadow:0 2px 8px rgba(30,64,175,.08); border:1px solid rgba(59,130,246,.12); transition:border-color .2s,box-shadow .2s; }
          .search-input-wrapper:focus-within { border-color:rgba(59,130,246,.4); box-shadow:0 2px 12px rgba(30,64,175,.14); }
          .search-input-wrapper input[type="text"] { flex:1; border:none; outline:none; font-size:13px; font-weight:500; color:#1f2937; background:transparent; }
          .search-input-wrapper input[type="text"]::placeholder { color:#9ca3af; font-weight:400; }
          .search-clear-btn { background:none; border:none; cursor:pointer; color:#9ca3af; padding:2px 4px; border-radius:4px; display:flex; align-items:center; transition:color .2s; }
          .search-clear-btn:hover { color:#6b7280; }
          .award-action-bar { animation:bulkBarIn .22s ease; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px; background:linear-gradient(135deg,#1e40af,#3b82f6); border-radius:14px; padding:12px 18px; margin-bottom:20px; box-shadow:0 4px 16px rgba(59,130,246,.3); }
          .award-action-info { font-size:13px; font-weight:700; color:white; display:flex; align-items:center; gap:6px; }
          .award-action-info span { opacity:.9; }
          .bulk-action-btn { display:inline-flex; align-items:center; gap:6px; padding:7px 14px; border-radius:10px; font-size:12px; font-weight:600; cursor:pointer; border:none; transition:all .15s; }
          .bulk-action-btn.primary { background:white; color:#1e40af; }
          .bulk-action-btn.primary:hover:not(:disabled) { background:#eff6ff; }
          .bulk-action-btn.ghost { background:rgba(255,255,255,.15); color:white; border:1px solid rgba(255,255,255,.3); }
          .bulk-action-btn.ghost:hover:not(:disabled) { background:rgba(255,255,255,.25); }
          .bulk-action-btn.swap { background:linear-gradient(135deg,#f59e0b,#d97706); color:white; box-shadow:0 2px 8px rgba(245,158,11,.35); }
          .bulk-action-btn.swap:hover:not(:disabled) { background:linear-gradient(135deg,#fbbf24,#f59e0b); }
          .bulk-action-btn:disabled { opacity:.45; cursor:not-allowed; }
          .award-layout-row { display:flex; gap:20px; flex-wrap:wrap; align-items:flex-start; margin-bottom:20px; }
          .award-layout-left { flex-grow:1; flex-shrink:1; min-width:0; transition:flex-basis .45s cubic-bezier(.4,0,.2,1); }
          .award-layout-left.is-full { flex-basis:100%; }
          .award-layout-left.is-shared { flex-basis:380px; }
          .award-layout-right {
            flex-grow:1; flex-shrink:1; min-width:0; overflow:hidden;
            transition:flex-basis .45s cubic-bezier(.4,0,.2,1), opacity .35s ease, transform .45s cubic-bezier(.4,0,.2,1);
          }
          .award-layout-right.is-visible { flex-basis:320px; opacity:1; transform:translateX(0); }
          .award-layout-right.is-hidden { flex-basis:0; opacity:0; transform:translateX(12px); pointer-events:none; }
        `}</style>

        <GlobalHeaderTitle
          title="Award Role"
          subtitle={
            selectedExamType
              ? `${selectedExamType.label}${selectedClass ? ` • ${selectedClass.label}` : ''}${selectedSection ? ` • Section ${selectedSection}` : ''}`
              : 'Select exam type to begin'
          }
          icon="pi pi-star"
        />

        <div className="award-layout-row">
        <div className={`award-layout-left ${showStats ? 'is-shared' : 'is-full'}`}>
        <div
          style={{
            background: 'white',
            borderRadius: 16,
            padding: '16px 20px',
            marginBottom: 20,
            boxShadow: '0 2px 8px rgba(30, 64, 175, 0.08)',
            border: '1px solid rgba(59, 130, 246, 0.12)',
          }}
        >
          <label
            style={{
              display: 'block',
              fontSize: 13,
              fontWeight: 600,
              color: '#1e40af',
              marginBottom: 10,
              letterSpacing: '0.02em',
            }}
          >
            Select Exam Type
          </label>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {examTypesLoading ? (
              <div style={{ padding: '6px 14px', color: '#64748b', fontSize: 13, fontWeight: 500 }}>
                Loading exam types...
              </div>
            ) : examTypesError ? (
              <div style={{ padding: '6px 14px', color: '#ef4444', fontSize: 13, fontWeight: 500 }}>
                {examTypesError}
              </div>
            ) : examTypes.length === 0 ? (
              <div style={{ padding: '6px 14px', color: '#64748b', fontSize: 13, fontWeight: 500 }}>
                No exam types available
              </div>
            ) : (
              examTypes.map((type) => (
                <button
                  key={type.value}
                  onClick={() => handleExamTypeChange(type.value)}
                  className="chip-button"
                  style={{
                    padding: '6px 14px',
                    border: selectedExamTypeId === type.value ? '1.5px solid #3b82f6' : '1.5px solid #e5e7eb',
                    borderRadius: 8,
                    background:
                      selectedExamTypeId === type.value
                        ? 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)'
                        : 'white',
                    color: selectedExamTypeId === type.value ? 'white' : '#1f2937',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    outline: 'none',
                    boxShadow: selectedExamTypeId === type.value ? '0 2px 8px rgba(59, 130, 246, 0.25)' : 'none',
                  }}
                >
                  {type.label}
                </button>
              ))
            )}
          </div>
        </div>

        {selectedExamTypeId && (
          <div
            style={{
              background: 'white',
              borderRadius: 16,
              padding: '16px 20px',
              marginBottom: 20,
              boxShadow: '0 2px 8px rgba(30, 64, 175, 0.08)',
              border: '1px solid rgba(59, 130, 246, 0.12)',
            }}
          >
            <div style={{ marginBottom: hasSections ? 16 : 0 }}>
              <label
                style={{
                  display: 'block',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#1e40af',
                  marginBottom: 10,
                  letterSpacing: '0.02em',
                }}
              >
                Select Class
              </label>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {isFetchingClasses ? (
                  <div style={{ padding: '6px 14px', color: '#64748b', fontSize: 13, fontWeight: 500 }}>
                    Loading classes...
                  </div>
                ) : classOptions.length === 0 ? (
                  <div style={{ padding: '6px 14px', color: '#64748b', fontSize: 13, fontWeight: 500 }}>
                    No classes available
                  </div>
                ) : (
                  classOptions.map((cls) => (
                    <button
                      key={cls.id}
                      onClick={() => handleClassChange(cls.id)}
                      className="chip-button"
                      style={{
                        padding: '6px 16px',
                        border: selectedClassId === cls.id ? '1.5px solid #3b82f6' : '1.5px solid #e5e7eb',
                        borderRadius: 8,
                        background:
                          selectedClassId === cls.id
                            ? 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)'
                            : 'white',
                        color: selectedClassId === cls.id ? 'white' : '#1f2937',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                        outline: 'none',
                        boxShadow: selectedClassId === cls.id ? '0 2px 8px rgba(59, 130, 246, 0.25)' : 'none',
                      }}
                    >
                      {cls.label}
                    </button>
                  ))
                )}
              </div>
            </div>

            {hasSections && (
              <div style={{ paddingTop: 16, borderTop: '1px solid #f3f4f6' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#1e40af',
                    marginBottom: 10,
                    letterSpacing: '0.02em',
                  }}
                >
                  Select Section
                </label>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {sections.map((section) => (
                    <button
                      key={section}
                      onClick={() => handleSectionChange(section)}
                      className="chip-button"
                      style={{
                        padding: '8px 16px',
                        border: selectedSection === section ? '1.5px solid #3b82f6' : '1.5px solid #e5e7eb',
                        borderRadius: 8,
                        background:
                          selectedSection === section
                            ? 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)'
                            : 'white',
                        color: selectedSection === section ? 'white' : '#1f2937',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        outline: 'none',
                        boxShadow: selectedSection === section ? '0 2px 8px rgba(59, 130, 246, 0.25)' : 'none',
                      }}
                    >
                      Section {section}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        </div>

        <div className={`award-layout-right ${showStats ? 'is-visible' : 'is-hidden'}`}>
          <div
            style={{
              background: 'white',
              borderRadius: 16,
              padding: '16px 20px',
              marginBottom: 20,
              boxShadow: '0 2px 8px rgba(30, 64, 175, 0.08)',
              border: '1px solid rgba(59, 130, 246, 0.12)',
            }}
          >
            <label
              style={{
                display: 'block',
                fontSize: 13,
                fontWeight: 600,
                color: '#1e40af',
                marginBottom: 14,
                letterSpacing: '0.02em',
              }}
            >
              Class Stats
            </label>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                gap: 12,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '12px 14px',
                  borderRadius: 12,
                  background: 'linear-gradient(135deg,rgba(65,105,225,.06),rgba(91,124,230,.04))',
                  border: '1px solid rgba(65,105,225,.12)',
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 9,
                    background: 'linear-gradient(135deg,#1e40af,#3b82f6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <i className="pi pi-users" style={{ fontSize: 14, color: 'white' }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#1f2937', lineHeight: 1.2 }}>
                    {classStats.totalStudents}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 500 }}>Total Students</div>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '12px 14px',
                  borderRadius: 12,
                  background: 'rgba(16,185,129,.05)',
                  border: '1px solid rgba(16,185,129,.12)',
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 9,
                    background: 'linear-gradient(135deg,#065f46,#10b981)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <i className="pi pi-book" style={{ fontSize: 14, color: 'white' }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#1f2937', lineHeight: 1.2 }}>
                    {classStats.totalSubjects}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 500 }}>Total Subjects</div>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '12px 14px',
                  borderRadius: 12,
                  background: 'rgba(34,197,94,.05)',
                  border: '1px solid rgba(34,197,94,.12)',
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 9,
                    background: 'linear-gradient(135deg,#166534,#22c55e)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <i className="pi pi-check-circle" style={{ fontSize: 14, color: 'white' }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#1f2937', lineHeight: 1.2 }}>
                    {classStats.fullyRecorded}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 500 }}>Fully Recorded</div>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '12px 14px',
                  borderRadius: 12,
                  background: 'rgba(234,179,8,.05)',
                  border: '1px solid rgba(234,179,8,.12)',
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 9,
                    background: 'linear-gradient(135deg,#854d0e,#eab308)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <i className="pi pi-exclamation-circle" style={{ fontSize: 14, color: 'white' }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#1f2937', lineHeight: 1.2 }}>
                    {classStats.partiallyRecorded}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 500 }}>Partially Recorded</div>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '12px 14px',
                  borderRadius: 12,
                  background: 'rgba(107,114,128,.05)',
                  border: '1px solid rgba(107,114,128,.12)',
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 9,
                    background: 'linear-gradient(135deg,#4b5563,#9ca3af)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <i className="pi pi-minus-circle" style={{ fontSize: 14, color: 'white' }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#1f2937', lineHeight: 1.2 }}>
                    {classStats.notRecorded}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 500 }}>Not Recorded</div>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '12px 14px',
                  borderRadius: 12,
                  background: 'rgba(20,184,166,.05)',
                  border: '1px solid rgba(20,184,166,.12)',
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 9,
                    background: 'linear-gradient(135deg,#0f766e,#14b8a6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <i className="pi pi-file-check" style={{ fontSize: 14, color: 'white' }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#1f2937', lineHeight: 1.2 }}>
                    {subjectStats.subjectsTouched} / {subjectStats.totalSubjects}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 500 }}>Subjects Recorded</div>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '12px 14px',
                  borderRadius: 12,
                  background: 'rgba(249,115,22,.05)',
                  border: '1px solid rgba(249,115,22,.12)',
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 9,
                    background: 'linear-gradient(135deg,#c2410c,#f97316)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <i className="pi pi-clock" style={{ fontSize: 14, color: 'white' }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#1f2937', lineHeight: 1.2 }}>
                    {subjectStats.subjectsUntouched} / {subjectStats.totalSubjects}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 500 }}>Subjects Pending</div>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '12px 14px',
                  borderRadius: 12,
                  background: 'rgba(139,92,246,.05)',
                  border: '1px solid rgba(139,92,246,.12)',
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 9,
                    background: 'linear-gradient(135deg,#5b21b6,#8b5cf6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <i className="pi pi-percentage" style={{ fontSize: 14, color: 'white' }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#1f2937', lineHeight: 1.2 }}>
                    {classStats.averagePercentage !== null ? `${classStats.averagePercentage.toFixed(1)}%` : '—'}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 500 }}>Class Average</div>
                </div>
              </div>
            </div>

            {classStats.totalSlots > 0 && (
              <div style={{ marginTop: 16 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 6,
                  }}
                >
                  <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Overall Completion</span>
                  <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>
                    {classStats.completionPercentage?.toFixed(1)}%
                  </span>
                </div>
                <div style={{ width: '100%', height: 8, borderRadius: 999, background: '#f3f4f6', overflow: 'hidden' }}>
                  <div
                    style={{
                      width: `${classStats.completionPercentage ?? 0}%`,
                      height: '100%',
                      borderRadius: 999,
                      background: 'linear-gradient(90deg,#0f766e,#14b8a6)',
                      transition: 'width .3s ease',
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        </div>

        {selectedExamTypeId && selectedClassId && allSubjects.length > 0 && (
          <div className="award-action-bar">
            <div className="award-action-info">
              <i className="pi pi-table" style={{ fontSize: 13 }} />
              {allSubjects.length} subject{allSubjects.length !== 1 ? 's' : ''}{' '}
              <span>in this class</span>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="bulk-action-btn primary" onClick={() => setCombinerOpen(true)}>
                <i className="pi pi-sitemap" style={{ fontSize: 12 }} />
                Combine Subjects{subjectGroups.length > 0 ? ` (${subjectGroups.length})` : ''}
              </button>

              <button
                className={`bulk-action-btn ${excludedSubjectIds.length > 0 ? 'swap' : 'ghost'}`}
                onClick={() => setExcluderOpen(true)}
              >
                <i className="pi pi-eye-slash" style={{ fontSize: 12 }} />
                Exclude Subjects{excludedSubjectIds.length > 0 ? ` (${excludedSubjectIds.length})` : ''}
              </button>

              <button
                className="bulk-action-btn ghost"
                onClick={openClassPrint}
                disabled={studentRows.length === 0}
              >
                <i className="pi pi-print" style={{ fontSize: 12 }} />
                Print Class Result
              </button>
            </div>
          </div>
        )}

        {selectedExamTypeId && selectedClassId && (
          <div className="search-input-wrapper">
            <i className="pi pi-search" style={{ fontSize: 15, color: '#3b82f6', flexShrink: 0 }} />
            <input
              type="text"
              placeholder="Search by name or roll no..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="search-clear-btn" onClick={() => setSearchQuery('')}>
                <i className="pi pi-times" style={{ fontSize: 12 }} />
              </button>
            )}
          </div>
        )}

        {selectedExamTypeId && selectedClassId && (
          loading ? (
            <StudentsListSkeletonLoader />
          ) : (
            <div
              style={{
                background: 'white',
                borderRadius: 20,
                boxShadow: '0 2px 12px rgba(0,0,0,.06)',
                border: '1px solid #e5e7eb',
                overflow: 'hidden',
                width: '100%',
              }}
            >
              {filteredStudentRows.length > 0 && (
                <div
                  style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid #f3f4f6',
                    background: 'linear-gradient(135deg,#ffffff,#fafbfc)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderRadius: '20px 20px 0 0',
                    flexWrap: 'wrap',
                    gap: 10,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        background: 'linear-gradient(135deg,rgba(65,105,225,.1),rgba(91,124,230,.08))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <i className="pi pi-list" style={{ fontSize: 14, color: '#4169E1' }} />
                    </div>
                    <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>
                      {filteredStudentRows.length} of {studentRows.length} student{studentRows.length !== 1 ? 's' : ''} ranked
                      {searchQuery.trim() && <span style={{ color: '#3b82f6' }}> for "{searchQuery}"</span>}
                      {selectedSection && !searchQuery.trim() && (
                        <span style={{ color: '#10b981' }}> (Section {selectedSection})</span>
                      )}
                    </span>
                  </div>
                </div>
              )}

              <DataTable
                value={filteredStudentRows}
                emptyMessage={
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '60px 20px',
                      minHeight: 400,
                    }}
                  >
                    <div
                      style={{
                        width: 56,
                        height: 56,
                        background: 'linear-gradient(135deg,#1e40af,#3b82f6,#60a5fa)',
                        borderRadius: 14,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 20,
                        boxShadow: '0 6px 18px rgba(59,130,246,.2)',
                      }}
                    >
                      <i className={emptyState.icon} style={{ fontSize: 25, color: 'white' }} />
                    </div>
                    <h3
                      style={{
                        margin: 0,
                        color: '#1e40af',
                        fontSize: 20,
                        fontWeight: 700,
                        marginBottom: 8,
                        textAlign: 'center',
                      }}
                    >
                      {emptyState.title}
                    </h3>
                    <p
                      style={{
                        margin: 0,
                        color: '#64748b',
                        fontSize: 14,
                        fontWeight: 500,
                        textAlign: 'center',
                        maxWidth: 400,
                        lineHeight: 1.6,
                      }}
                    >
                      {emptyState.message}
                    </p>
                  </div>
                }
              >
                <Column header="Roll No" body={rollNoTemplate} style={{ width: 80 }} />
                <Column header="Student" body={studentNameTemplate} />
                <Column header="Rank" body={rankTemplate} style={{ width: 60 }} />
                <Column header="Marks Recorded" body={marksRecordedTemplate} style={{ width: 130 }} />
                <Column header="Total Marks" body={totalMarksTemplate} style={{ width: 130 }} />
                <Column header="Percentage" body={percentageTemplate} style={{ width: 110 }} />
                <Column header="Print" body={printTemplate} style={{ width: 60 }} />
              </DataTable>
            </div>
          )
        )}
      </div>

      <SubjectCombinerModal
        visible={combinerOpen}
        onHide={() => setCombinerOpen(false)}
        subjects={allSubjects}
        groups={subjectGroups}
        onChange={handleSubjectGroupsChange}
      />

      <SubjectExcluderModal
        visible={excluderOpen}
        onHide={() => setExcluderOpen(false)}
        subjects={allSubjects}
        excludedIds={excludedSubjectIds}
        onChange={handleExcludedSubjectsChange}
      />

      <PrintOptionsModal
        visible={printOpen}
        onHide={() => setPrintOpen(false)}
        title={printTarget === 'class' ? 'Print Class Result' : 'Print Report Card'}
        subtitle={
          printTarget === 'class'
            ? `Class ${selectedClass?.label ?? ''}${selectedSection ? ` · ${selectedSection}` : ''} · ${studentRows.length} student${studentRows.length !== 1 ? 's' : ''}`
            : printTarget.student_name
        }
        onPrint={handlePrint}
      />
    </>
  );
};

export default AwardRole;